import { z } from "zod";
import {
  FirestoreTimestampSchema,
  RequiredFirestoreTimestampSchema,
} from "./common.schema";

/**
 * Idea status enum
 */
export const IdeaStatusSchema = z.enum([
  "pending",
  "completed",
  "error",
  "archived",
  "implemented",
]);
export type IdeaStatus = z.infer<typeof IdeaStatusSchema>;

/**
 * Idea document schema (without id)
 * Path: organizations/{orgId}/domains/{domainId}/ideas/{ideaId}
 */
export const IdeaDocSchema = z.object({
  user_id: z.string(),
  domain_id: z.string(),
  status: IdeaStatusSchema.default("completed"),
  title: z.string().default(""),
  description: z.string().default(""),
  target_keyword: z.string().default(""),
  suggested_slug: z.string().default(""),
  rationale: z.string().default(""),
  target_audience: z.string().default(""),
  content_outline: z.array(z.string()).default([]),
  source_input: z.string().default(""),
  error_message: z.string().optional().default(""),
  implemented_page_id: z.string().nullable().optional(),
  implemented_page_type: z.enum(["post", "page"]).nullable().optional(),
  implemented_at: FirestoreTimestampSchema.optional(),
  created_at: RequiredFirestoreTimestampSchema,
  updated_at: FirestoreTimestampSchema.optional(),
  // Which conversation turn produced this document, when the assistant made it.
  // Backend-written only (these collections are `allow write: if false`), so the
  // value is trustworthy; null on anything a member made by hand and on
  // documents written before the fields existed.
  source_thread_id: z.string().nullable().optional(),
  source_message_id: z.string().nullable().optional(),
});
export type IdeaDoc = z.infer<typeof IdeaDocSchema>;
export type EditableIdeaFields = Pick<
  IdeaDoc,
  | "title"
  | "description"
  | "target_keyword"
  | "suggested_slug"
  | "rationale"
  | "target_audience"
  | "content_outline"
  | "source_input"
>;

/**
 * Idea schema (with id)
 */
export const IdeaSchema = IdeaDocSchema.extend({ id: z.string() });
export type Idea = z.infer<typeof IdeaSchema>;

/**
 * Parse functions
 */
export const parseIdea = (data: unknown): Idea => IdeaSchema.parse(data);
export const safeIdeaParse = (data: unknown) => IdeaSchema.safeParse(data);

// UI State type
export interface IdeaState {
  ideas: Idea[];
  isLoadingIdeas: boolean;
  ideasError: string | null;
}
